// ============================================================================
// AVAILABILITY SERVICE
// ============================================================================
// Business logic and API calls for Availability module

import { get, post } from '../client/axiosClient';
import { availabilityEndpoints } from '../endpoints/availability.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import { dayKey } from 'utils/calendarDate';
import type { ApiResponse } from '../types';
import type {
  AvailabilityRequest,
  AvailabilityResponse,
  InventoryCalendarResponse,
  OccupancyHorizonResponse,
} from '../types/availability.types';

/**
 * Availability Service Class
 */
class AvailabilityService {
  /**
   * Check room availability
   */
  async checkAvailability(data: AvailabilityRequest): Promise<ApiResponse<AvailabilityResponse>> {
    const response = await post<ApiResponse<AvailabilityResponse>>(
      availabilityEndpoints.check(),
      data
    );
    return response.data;
  }

  /**
   * Get inventory calendar for a property/month
   */
  async getCalendar(
    propertyId: string,
    year: number,
    month: number
  ): Promise<ApiResponse<InventoryCalendarResponse>> {
    const endpoint = buildEndpointWithQuery('/Availability/calendar', {
      propertyId,
      year,
      month,
    });
    const response = await get<ApiResponse<InventoryCalendarResponse>>(endpoint);
    return response.data;
  }

  /**
   * Rolling free-inventory horizon for the Booking Worksheet strip.
   * `from` defaults to today server-side; `days` is clamped to 1..62 there.
   */
  async getOccupancyHorizon(
    propertyId: string,
    from: string,
    days: number
  ): Promise<ApiResponse<OccupancyHorizonResponse>> {
    const endpoint = buildEndpointWithQuery('/Availability/occupancy-horizon', {
      propertyId,
      from,
      days,
    });
    const response = await get<ApiResponse<any>>(endpoint);
    const raw = response.data;
    /* The API sends C# DateTime ("2026-08-17T00:00:00"). Normalise to a bare
       yyyy-MM-dd HERE, once, so no component has to know the difference — a
       component appending 'T00:00:00' to the full form produced Invalid Date,
       which surfaced as NaN in every strip cell. */
    if (raw?.data?.days) {
      raw.data = {
        ...raw.data,
        from: dayKey(raw.data.from),
        days: raw.data.days.map((d: any) => ({ ...d, date: dayKey(d.date) })),
      };
    }
    return raw as ApiResponse<OccupancyHorizonResponse>;
  }
}

/**
 * Export singleton instance
 */
export const availabilityService = new AvailabilityService();
export default availabilityService;
